// AudioCapture — browser audio VAD + recording + playback
// Uses Web Audio API: getUserMedia → AudioContext → AnalyserNode

const VAD_START_THRESHOLD = 0.02   // RMS threshold to detect speech start
const VAD_START_DURATION = 200     // ms above threshold to confirm speech start
const VAD_SILENCE_DURATION = 1200  // ms of silence to confirm speech end
const SAMPLE_RATE = 16000          // target sample rate

export class AudioCapture {
  constructor() {
    this.audioContext = null
    this.analyser = null
    this.mediaStream = null
    this.scriptProcessor = null

    this.isRecording = false
    this.recordedChunks = [] // Float32Array chunks
    this.silenceStart = 0
    this.speechDetected = false
    this.analyserData = null

    // Callbacks
    this.onSpeechStart = null   // () => void
    this.onSpeechEnd = null     // (wavBlob: Blob) => void
    this.onPlayEnd = null       // () => void
    this.onVADLevel = null      // (rms: number) => void — optional debug

    this._rafId = null
    this._currentAudio = null
  }

  /** Request mic permission and start VAD monitoring loop */
  async start() {
    if (this.audioContext) return // already running

    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: { ideal: SAMPLE_RATE },
        echoCancellation: true,
        noiseSuppression: true,
      },
    })

    this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: SAMPLE_RATE,
    })

    // iOS Safari starts AudioContext in suspended state — must resume on user gesture
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
    }

    const source = this.audioContext.createMediaStreamSource(this.mediaStream)
    this.analyser = this.audioContext.createAnalyser()
    this.analyser.fftSize = 1024
    this.analyser.smoothingTimeConstant = 0.3

    // ScriptProcessor for capturing raw PCM while recording
    // Note: AudioWorklet is preferred but ScriptProcessor is simpler for MVP
    this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1)

    source.connect(this.analyser)
    source.connect(this.scriptProcessor)
    this.scriptProcessor.connect(this.audioContext.destination)

    this.analyserData = new Float32Array(this.analyser.fftSize)

    this.scriptProcessor.onaudioprocess = (event) => {
      if (this.isRecording) {
        const inputData = event.inputBuffer.getChannelData(0)
        this.recordedChunks.push(new Float32Array(inputData))
      }
    }

    // Start VAD polling loop
    this._pollVAD()
  }

  /** VAD polling via requestAnimationFrame */
  _pollVAD() {
    this._rafId = requestAnimationFrame(() => this._pollVAD())

    if (!this.analyser) return
    this.analyser.getFloatTimeDomainData(this.analyserData)

    let sumSquares = 0
    for (let i = 0; i < this.analyserData.length; i++) {
      sumSquares += this.analyserData[i] * this.analyserData[i]
    }
    const rms = Math.sqrt(sumSquares / this.analyserData.length)

    if (this.onVADLevel) this.onVADLevel(rms)

    const now = performance.now()

    if (!this.isRecording) {
      // Not recording — wait for speech start
      if (rms > VAD_START_THRESHOLD) {
        if (!this.speechDetected) {
          this.speechDetected = true
          this.silenceStart = 0 // reset
          setTimeout(() => {
            if (this.speechDetected && !this.isRecording) {
              // Confirmed speech start after VAD_START_DURATION
              this.isRecording = true
              this.recordedChunks = []
              if (this.onSpeechStart) this.onSpeechStart()
            }
          }, VAD_START_DURATION)
        }
      } else {
        this.speechDetected = false
      }
    } else {
      // Recording — wait for silence
      if (rms < VAD_START_THRESHOLD) {
        if (this.silenceStart === 0) {
          this.silenceStart = now
        } else if (now - this.silenceStart > VAD_SILENCE_DURATION) {
          // Confirmed speech end
          this.isRecording = false
          this.speechDetected = false
          this.silenceStart = 0
          const wavBlob = this._exportWAV()
          this.recordedChunks = []
          if (this.onSpeechEnd) this.onSpeechEnd(wavBlob)
        }
      } else {
        this.silenceStart = 0 // still talking, reset silence timer
      }
    }
  }

  /** Export recorded PCM chunks as 16kHz mono 16-bit PCM WAV Blob */
  _exportWAV() {
    // Merge all chunks
    let totalLength = 0
    for (const chunk of this.recordedChunks) {
      totalLength += chunk.length
    }
    const merged = new Float32Array(totalLength)
    let offset = 0
    for (const chunk of this.recordedChunks) {
      merged.set(chunk, offset)
      offset += chunk.length
    }

    // Downsample to 16kHz if needed
    const actualRate = this.audioContext.sampleRate
    let samples
    if (actualRate !== SAMPLE_RATE) {
      const ratio = actualRate / SAMPLE_RATE
      const newLen = Math.floor(merged.length / ratio)
      samples = new Int16Array(newLen)
      for (let i = 0; i < newLen; i++) {
        const srcIdx = Math.floor(i * ratio)
        const s = Math.max(-1, Math.min(1, merged[srcIdx]))
        samples[i] = s < 0 ? s * 0x8000 : s * 0x7fff
      }
    } else {
      samples = new Int16Array(merged.length)
      for (let i = 0; i < merged.length; i++) {
        const s = Math.max(-1, Math.min(1, merged[i]))
        samples[i] = s < 0 ? s * 0x8000 : s * 0x7fff
      }
    }

    // Build WAV header
    const dataLen = samples.length * 2
    const buffer = new ArrayBuffer(44 + dataLen)
    const view = new DataView(buffer)

    // RIFF header
    writeString(view, 0, 'RIFF')
    view.setUint32(4, 36 + dataLen, true)
    writeString(view, 8, 'WAVE')
    // fmt chunk
    writeString(view, 12, 'fmt ')
    view.setUint32(16, 16, true)               // chunk size
    view.setUint16(20, 1, true)                // PCM format
    view.setUint16(22, 1, true)                // mono
    view.setUint32(24, SAMPLE_RATE, true)      // sample rate
    view.setUint32(28, SAMPLE_RATE * 2, true)  // byte rate
    view.setUint16(32, 2, true)                // block align
    view.setUint16(34, 16, true)               // bits per sample
    // data chunk
    writeString(view, 36, 'data')
    view.setUint32(40, dataLen, true)

    // Write PCM samples
    const int16View = new Int16Array(buffer, 44, samples.length)
    int16View.set(samples)

    return new Blob([buffer], { type: 'audio/wav' })
  }

  /** Play base64-encoded WAV audio, returns a Promise that resolves on end */
  playAudio(base64) {
    return new Promise((resolve) => {
      const audio = new Audio(`data:audio/wav;base64,${base64}`)
      this._currentAudio = audio
      audio.onended = () => {
        this._currentAudio = null
        if (this.onPlayEnd) this.onPlayEnd()
        resolve()
      }
      audio.onerror = () => {
        this._currentAudio = null
        resolve()
      }
      audio.play().catch(() => resolve())
    })
  }

  /** Interrupt current playback */
  stopPlayback() {
    if (this._currentAudio) {
      this._currentAudio.pause()
      this._currentAudio = null
    }
  }

  /** Release mic and stop monitoring */
  stop() {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId)
      this._rafId = null
    }
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect()
      this.scriptProcessor = null
    }
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop())
      this.mediaStream = null
    }
    this.isRecording = false
    this.recordedChunks = []
  }
}

function writeString(view, offset, str) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i))
  }
}
